import React, { useState, useEffect, useRef } from 'react';
import { DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    TextField,
    InputAdornment,
    Alert,
} from '@mui/material';
import { Close, GpsFixed, LocationOn, Search as SearchIcon } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon paths broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl:       require('leaflet/dist/images/marker-icon.png'),
    shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

const DEFAULT_CENTER = [20.5937, 78.9629]; // India
const DEFAULT_ZOOM   = 5;
const DETAIL_ZOOM    = 16;

async function nominatimReverse(lat, lng) {
    try {
        const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`,
            { headers: { 'Accept-Language': 'en' } }
        );
        const d = await r.json();
        return d?.display_name || null;
    } catch {
        return null;
    }
}

async function nominatimSearch(query) {
    try {
        const r = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1&countrycodes=in`,
            { headers: { 'Accept-Language': 'en' } }
        );
        const d = await r.json();
        return d && d.length > 0 ? d[0] : null;
    } catch {
        return null;
    }
}

const LocationPicker = ({ open, onClose, lat, lng, onConfirm }) => {
    const mapDivRef  = useRef(null);
    const mapRef     = useRef(null);
    const markerRef  = useRef(null);
    const placeRef   = useRef(null);

    const [pos,         setPos]         = useState(null);
    const [label,       setLabel]       = useState('');
    const [geocoding,   setGeocoding]   = useState(false);
    const [gettingGPS,  setGettingGPS]  = useState(false);
    const [gpsError,    setGpsError]    = useState('');
    const [searchText,  setSearchText]  = useState('');
    const [searching,   setSearching]   = useState(false);
    const [searchError, setSearchError] = useState('');

    placeRef.current = async (latVal, lngVal) => {
        if (!mapRef.current) return;
        const newPos = [latVal, lngVal];
        setPos(newPos);
        setGeocoding(true);
        setLabel('');

        if (markerRef.current) {
            markerRef.current.setLatLng(newPos);
        } else {
            const m = L.marker(newPos, { draggable: true }).addTo(mapRef.current);
            m.on('dragend', (e) => {
                const ll = e.target.getLatLng();
                placeRef.current(ll.lat, ll.lng);
            });
            markerRef.current = m;
        }
        mapRef.current.flyTo(newPos, DETAIL_ZOOM, { animate: true, duration: 0.8 });

        const addr = await nominatimReverse(latVal, lngVal);
        setLabel(addr || `${latVal.toFixed(5)}, ${lngVal.toFixed(5)}`);
        setGeocoding(false);
    };

    // Initialise map — wait 400ms for MUI dialog open animation to finish
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => {
            if (!mapDivRef.current || mapRef.current) return;

            const initLat = lat ? parseFloat(lat) : null;
            const initLng = lng ? parseFloat(lng) : null;
            const center  = (initLat && initLng) ? [initLat, initLng] : DEFAULT_CENTER;
            const zoom    = (initLat && initLng) ? DETAIL_ZOOM : DEFAULT_ZOOM;

            const map = L.map(mapDivRef.current, { center, zoom });
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            }).addTo(map);
            map.on('click', (e) => placeRef.current(e.latlng.lat, e.latlng.lng));
            mapRef.current = map;
            // Force Leaflet to recalculate container size
            map.invalidateSize();

            if (initLat && initLng) placeRef.current(initLat, initLng);
        }, 400);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Destroy map when dialog closes
    useEffect(() => {
        if (open) return;
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current    = null;
            markerRef.current = null;
        }
        setPos(null);
        setLabel('');
        setGeocoding(false);
        setGpsError('');
        setSearchText('');
        setSearchError('');
    }, [open]);

    const handleGPS = () => {
        if (!navigator.geolocation) {
            setGpsError('Geolocation is not supported by this browser.');
            return;
        }
        setGpsError('');
        setGettingGPS(true);
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                placeRef.current(coords.latitude, coords.longitude);
                setGettingGPS(false);
            },
            (err) => {
                setGettingGPS(false);
                if (err.code === 1) {
                    setGpsError('GPS blocked — Chrome requires HTTPS for location access on a network IP. Use the search box instead.');
                } else {
                    setGpsError('Could not get location: ' + err.message);
                }
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleSearch = async () => {
        const q = searchText.trim();
        if (!q) return;
        setSearching(true);
        setSearchError('');
        const result = await nominatimSearch(q);
        setSearching(false);
        if (!result) {
            setSearchError('Place not found. Try a more specific address.');
            return;
        }
        placeRef.current(parseFloat(result.lat), parseFloat(result.lon));
    };

    const handleConfirm = () => {
        if (!pos) return;
        onConfirm({ lat: pos[0].toFixed(6), lng: pos[1].toFixed(6), label });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 1500 }}>
            <DialogTitle sx={{ ...DIALOG_TITLE_SX }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Pick Customer Location</Typography>
                    <IconButton onClick={onClose}><Close /></IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                {/* Search bar */}
                <Box sx={{ px: 2, pt: 1, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search area, street, city…"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSearch}
                            disabled={searching || !searchText.trim()}
                            sx={{ whiteSpace: 'nowrap', minWidth: 72 }}
                        >
                            {searching ? <CircularProgress size={16} color="inherit" /> : 'Go'}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={gettingGPS ? <CircularProgress size={14} /> : <GpsFixed />}
                            onClick={handleGPS}
                            disabled={gettingGPS}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {gettingGPS ? '…' : 'GPS'}
                        </Button>
                    </Box>
                    {searchError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{searchError}</Typography>
                    )}
                    {gpsError && (
                        <Alert severity="warning" sx={{ mt: 0.75, py: 0.25, fontSize: '0.75rem' }}>{gpsError}</Alert>
                    )}
                </Box>

                {/* Selected address label */}
                <Box sx={{ px: 2, py: 0.75, minHeight: 32, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
                    {pos ? (
                        <>
                            <LocationOn fontSize="small" color="success" />
                            {geocoding
                                ? <CircularProgress size={14} />
                                : <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
                            }
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Tap on the map to drop a pin, or search above.
                        </Typography>
                    )}
                </Box>

                {/* Map */}
                <Box sx={{ height: 380 }}>
                    <div ref={mapDivRef} style={{ height: '100%', width: '100%' }} />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!pos || geocoding}
                >
                    Use This Location
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LocationPicker;